import type { AppLocale } from './translations';

type PatternHandler = (matches: RegExpMatchArray) => Record<AppLocale, string>;

interface TranslationPattern {
  pattern: RegExp;
  handler: PatternHandler;
}

const keep = (value: string) => value;

const translateShareStatus = (value: string, locale: AppLocale): string => {
  const translations: Record<AppLocale, Record<string, string>> = {
    ko: {
      '화면·문서 공유 중': '화면·문서 공유 중',
      '화면 공유 중': '화면 공유 중',
      '문서 공유 중': '문서 공유 중',
      '스케치모드 공유 중': '스케치모드 공유 중',
    },
    en: {
      '화면·문서 공유 중': 'sharing screen and documents',
      '화면 공유 중': 'sharing their screen',
      '문서 공유 중': 'sharing a document',
      '스케치모드 공유 중': 'sharing in sketch mode',
    },
    ja: {
      '화면·문서 공유 중': '画面・文書共有中',
      '화면 공유 중': '画面共有中',
      '문서 공유 중': '文書共有中',
      '스케치모드 공유 중': 'スケッチモード共有中',
    },
  };

  return translations[locale][value] ?? value;
};

const patterns: TranslationPattern[] = [
  {
    pattern: /^(.+)님이 참석을 요청하였습니다\.$/,
    handler: ([, name]) => ({ ko: `${name}님이 참석을 요청하였습니다.`, en: `${name} requested to join.`, ja: `${name}さんが参加をリクエストしました。` }),
  },
  {
    pattern: /^(.+) 님을 내보내시겠습니까\?$/,
    handler: ([, name]) => ({ ko: `${name} 님을 내보내시겠습니까?`, en: `Do you want to remove ${name}?`, ja: `${name}さんを退出させますか？` }),
  },
  {
    pattern: /^(.+)님을\n회의에서 내보내시겠습니까\?$/,
    handler: ([, name]) => ({ ko: `${name}님을\n회의에서 내보내시겠습니까?`, en: `Do you want to remove ${name}\nfrom the meeting?`, ja: `${name}さんを\n会議から退出させますか？` }),
  },
  {
    pattern: /^#(.+)님을 내보냈습니다\.$/,
    handler: ([, name]) => ({ ko: `#${name}님을 내보냈습니다.`, en: `#${name} has been removed.`, ja: `#${name}さんを退出させました。` }),
  },
  {
    pattern: /^(.+)님께 마스터 권한을 부여하였습니다\.$/,
    handler: ([, name]) => ({ ko: `${name}님께 마스터 권한을 부여하였습니다.`, en: `Granted master permission to ${name}.`, ja: `${name}さんにマスター権限を付与しました。` }),
  },
  {
    pattern: /^(.+)님의 마스터 권한을 해제하였습니다\.$/,
    handler: ([, name]) => ({ ko: `${name}님의 마스터 권한을 해제하였습니다.`, en: `Removed master permission from ${name}.`, ja: `${name}さんのマスター権限を解除しました。` }),
  },
  {
    pattern: /^(.+)님의 마이크를 껐습니다\.$/,
    handler: ([, name]) => ({ ko: `${name}님의 마이크를 껐습니다.`, en: `Turned off ${name}'s microphone.`, ja: `${name}さんのマイクをオフにしました。` }),
  },
  {
    pattern: /^(.+)님의 카메라를 껐습니다\.$/,
    handler: ([, name]) => ({ ko: `${name}님의 카메라를 껐습니다.`, en: `Turned off ${name}'s camera.`, ja: `${name}さんのカメラをオフにしました。` }),
  },
  {
    pattern: /^(.+)님의 소리를 껐습니다\.$/,
    handler: ([, name]) => ({ ko: `${name}님의 소리를 껐습니다.`, en: `Muted ${name}'s sound for you.`, ja: `${name}さんの音声をオフにしました。` }),
  },
  {
    pattern: /^(.+)님의 소리를 다시 듣습니다\.$/,
    handler: ([, name]) => ({ ko: `${name}님의 소리를 다시 듣습니다.`, en: `You will hear ${name}'s sound again.`, ja: `${name}さんの音声を再び聞きます。` }),
  },
  {
    pattern: /^(.+)님의 발언이 기록됩니다\.$/,
    handler: ([, name]) => ({ ko: `${name}님의 발언이 기록됩니다.`, en: `${name}'s speech will be recorded.`, ja: `${name}さんの発言が記録されます。` }),
  },
  {
    pattern: /^(.+)님의 발언이 더이상 기록되지 않습니다\.$/,
    handler: ([, name]) => ({ ko: `${name}님의 발언이 더이상 기록되지 않습니다.`, en: `${name}'s speech will no longer be recorded.`, ja: `${name}さんの発言はこれ以上記録されません。` }),
  },
  {
    pattern: /^(.+)님의 공유 화면$/,
    handler: ([, name]) => ({ ko: `${name}님의 공유 화면`, en: `${name}'s shared screen`, ja: `${name}さんの共有画面` }),
  },
  {
    pattern: /^(.+)의 화면 공유$/,
    handler: ([, name]) => ({ ko: `${name}의 화면 공유`, en: `${name}'s screen share`, ja: `${name}の画面共有` }),
  },
  {
    pattern: /^(.+)의 문서 공유$/,
    handler: ([, name]) => ({ ko: `${name}의 문서 공유`, en: `${name}'s document share`, ja: `${name}の文書共有` }),
  },
  {
    pattern: /^(.+) 문서 공유$/,
    handler: ([, name]) => ({ ko: `${name} 문서 공유`, en: `${name} document sharing`, ja: `${name}文書共有` }),
  },
  {
    pattern: /^(.+)의 스케치 공유$/,
    handler: ([, name]) => ({ ko: `${name}의 스케치 공유`, en: `${name}'s sketch share`, ja: `${name}のスケッチ共有` }),
  },
  {
    pattern: /^(.+) 스케치$/,
    handler: ([, name]) => ({ ko: `${name} 스케치`, en: `${name} sketch`, ja: `${name}スケッチ` }),
  },
  {
    pattern: /^(.+)님 외 ([0-9,]+)명$/,
    handler: ([, name, count]) => ({ ko: `${name}님 외 ${count}명`, en: `${name} and ${count} others`, ja: `${name}さんほか${count}名` }),
  },
  {
    pattern: /^(.+)님 외 ([0-9,]+)명이 화면 공유중$/,
    handler: ([, name, count]) => ({ ko: `${name}님 외 ${count}명이 화면 공유중`, en: `${name} and ${count} others are sharing screens`, ja: `${name}さんほか${count}名が画面共有中` }),
  },
  {
    pattern: /^(.+)님이 화면 공유중$/,
    handler: ([, name]) => ({ ko: `${name}님이 화면 공유중`, en: `${name} is sharing their screen`, ja: `${name}さんが画面共有中` }),
  },
  {
    pattern: /^(.+)님 외 ([0-9,]+)명이 (화면·문서 공유 중|화면 공유 중|문서 공유 중|스케치모드 공유 중)$/,
    handler: ([, name, count, status]) => ({
      ko: `${name}님 외 ${count}명이 ${status}`,
      en: `${name} and ${count} others are ${translateShareStatus(status, 'en')}`,
      ja: `${name}さんほか${count}名が${translateShareStatus(status, 'ja')}`,
    }),
  },
  {
    pattern: /^(.+)님이 (화면·문서 공유 중|화면 공유 중|문서 공유 중|스케치모드 공유 중)$/,
    handler: ([, name, status]) => ({
      ko: `${name}님이 ${status}`,
      en: `${name} is ${translateShareStatus(status, 'en')}`,
      ja: `${name}さんが${translateShareStatus(status, 'ja')}`,
    }),
  },
  {
    pattern: /^(.+) 외 ([0-9,]+)명이$/,
    handler: ([, name, count]) => ({ ko: `${name} 외 ${count}명이`, en: `${name} and ${count} others are`, ja: `${name}ほか${count}名が` }),
  },
  {
    pattern: /^ 외 ([0-9,]+)명이$/,
    handler: ([, count]) => ({ ko: ` 외 ${count}명이`, en: ` and ${count} others`, ja: `ほか${count}名が` }),
  },
  {
    pattern: /^화면은 최대 ([0-9,]+)개까지 고정가능합니다\.$/,
    handler: ([, count]) => ({ ko: `화면은 최대 ${count}개까지 고정가능합니다.`, en: `You can pin up to ${count} screens.`, ja: `画面は最大${count}個まで固定できます。` }),
  },
  {
    pattern: /^화면이 고정되었습니다\. ([0-9,]+)\/([0-9,]+)$/,
    handler: ([, current, total]) => ({ ko: `화면이 고정되었습니다. ${current}/${total}`, en: `Screen pinned. ${current}/${total}`, ja: `画面を固定しました。${current}/${total}` }),
  },
  {
    pattern: /^화면 고정이 해제되었습니다\. ([0-9,]+)\/([0-9,]+)$/,
    handler: ([, current, total]) => ({ ko: `화면 고정이 해제되었습니다. ${current}/${total}`, en: `Screen unpinned. ${current}/${total}`, ja: `画面固定を解除しました。${current}/${total}` }),
  },
  {
    pattern: /^(.+)님을\n모두에게 화면 고정 하시겠습니까\?$/,
    handler: ([, name]) => ({ ko: `${name}님을\n모두에게 화면 고정 하시겠습니까?`, en: `Do you want to pin ${name}\nfor everyone?`, ja: `${name}さんを\n全員に画面固定しますか？` }),
  },
  {
    pattern: /^(.+)님의 공유 화면을\n모두에게 화면 고정 하시겠습니까\?$/,
    handler: ([, name]) => ({ ko: `${name}님의 공유 화면을\n모두에게 화면 고정 하시겠습니까?`, en: `Do you want to pin ${name}'s shared screen\nfor everyone?`, ja: `${name}さんの共有画面を\n全員に固定しますか？` }),
  },
  {
    pattern: /^(.+)을\n모두에게 화면 고정 하시겠습니까\?$/,
    handler: ([, name]) => ({ ko: `${name}을\n모두에게 화면 고정 하시겠습니까?`, en: `Do you want to pin ${name}\nfor everyone?`, ja: `${name}を\n全員に固定しますか？` }),
  },
  {
    pattern: /^마스터가 (.+)님의 공유 화면을 (.+)$/,
    handler: ([, name, action]) => ({ ko: `마스터가 ${name}님의 공유 화면을 ${action}`, en: `The master ${action} ${name}'s shared screen`, ja: `マスターが${name}さんの共有画面を${action}` }),
  },
  {
    pattern: /^마스터가 (.+)님 화면을 (.+)$/,
    handler: ([, name, action]) => ({ ko: `마스터가 ${name}님 화면을 ${action}`, en: `The master ${action} ${name}'s screen`, ja: `マスターが${name}さんの画面を${action}` }),
  },
  {
    pattern: /^회의 연결 실패: (.+)$/,
    handler: ([, reason]) => ({ ko: `회의 연결 실패: ${reason}`, en: `Meeting connection failed: ${reason}`, ja: `会議接続失敗: ${reason}` }),
  },
  {
    pattern: /^삭제 실패\((.+)\): (.+)$/,
    handler: ([, status, message]) => ({ ko: `삭제 실패(${status}): ${message}`, en: `Delete failed (${status}): ${message}`, ja: `削除失敗(${status}): ${message}` }),
  },
  {
    pattern: /^회의 자료 ([0-9,]+)건을 삭제하였습니다\.$/,
    handler: ([, count]) => ({ ko: `회의 자료 ${count}건을 삭제하였습니다.`, en: `${count} meeting material(s) have been deleted.`, ja: `会議資料${count}件を削除しました。` }),
  },
  {
    pattern: /^([0-9,]+)개 이메일 전송에 실패했습니다\.$/,
    handler: ([, count]) => ({ ko: `${count}개 이메일 전송에 실패했습니다.`, en: `Failed to send ${count} email(s).`, ja: `${count}件のメール送信に失敗しました。` }),
  },
  {
    pattern: /^총 ([0-9,]+)개의 파일이 있습니다\.$/,
    handler: ([, count]) => ({ ko: `총 ${count}개의 파일이 있습니다.`, en: `There are ${count} files in total.`, ja: `合計${count}個のファイルがあります。` }),
  },
  {
    pattern: /^파일 목록이 업데이트되었습니다\. 총 ([0-9,]+)개의 파일이 있습니다\.$/,
    handler: ([, count]) => ({ ko: `파일 목록이 업데이트되었습니다. 총 ${count}개의 파일이 있습니다.`, en: `The file list has been updated. There are ${count} files in total.`, ja: `ファイル一覧が更新されました。合計${count}個のファイルがあります。` }),
  },
  {
    pattern: /^최대 ([0-9,]+)개까지 추가가 가능합니다\.$/,
    handler: ([, count]) => ({ ko: `최대 ${count}개까지 추가가 가능합니다.`, en: `You can add up to ${count} items.`, ja: `最大${count}個まで追加できます。` }),
  },
  {
    pattern: /^([0-9]{1,2})시간$/,
    handler: ([, value]) => ({ ko: `${value}시간`, en: `${value}h`, ja: `${value}時間` }),
  },
  {
    pattern: /^([0-9]{1,2})분$/,
    handler: ([, value]) => ({ ko: `${value}분`, en: `${value}m`, ja: `${value}分` }),
  },
  {
    pattern: /^([0-9]{1,2})초$/,
    handler: ([, value]) => ({ ko: `${value}초`, en: `${value}s`, ja: `${value}秒` }),
  },
  {
    pattern: /^([0-9,]+)명$/,
    handler: ([, count]) => ({ ko: `${count}명`, en: `${count} people`, ja: `${count}名` }),
  },
  {
    pattern: /^([0-9,]+)건$/,
    handler: ([, count]) => ({ ko: `${count}건`, en: `${count} items`, ja: `${count}件` }),
  },
  {
    pattern: /^([0-9,]+)개$/,
    handler: ([, count]) => ({ ko: `${count}개`, en: `${count} items`, ja: `${count}個` }),
  },
  {
    pattern: /^([0-9,]+)쪽$/,
    handler: ([, count]) => ({ ko: `${count}쪽`, en: `${count} pages`, ja: `${count}ページ` }),
  },
  {
    pattern: /^([0-9,]+)명 이상$/,
    handler: ([, count]) => ({ ko: `${count}명 이상`, en: `${count}+ people`, ja: `${count}名以上` }),
  },
  {
    pattern: /^≤([0-9,]+)명: ([0-9,]+)p$/,
    handler: ([, count, resolution]) => ({ ko: `≤${count}명: ${resolution}p`, en: `≤${count} people: ${resolution}p`, ja: `≤${count}名: ${resolution}p` }),
  },
  {
    pattern: /^([0-9,]+)명\+: ([0-9,]+)p$/,
    handler: ([, count, resolution]) => ({ ko: `${count}명+: ${resolution}p`, en: `${count}+ people: ${resolution}p`, ja: `${count}名以上: ${resolution}p` }),
  },
  {
    pattern: /^자동 \(([0-9,]+)p\)$/,
    handler: ([, resolution]) => ({ ko: `자동 (${resolution}p)`, en: `Auto (${resolution}p)`, ja: `自動 (${resolution}p)` }),
  },
  {
    pattern: /^그리드 ([0-9,]+)타일 → ([0-9,]+)p$/,
    handler: ([, count, resolution]) => ({ ko: `그리드 ${count}타일 → ${resolution}p`, en: `Grid ${count} tiles -> ${resolution}p`, ja: `グリッド${count}タイル → ${resolution}p` }),
  },
  {
    pattern: /^메인 ([0-9,]+)p \/ 서브 ([0-9,]+)p$/,
    handler: ([, main, sub]) => ({ ko: `메인 ${main}p / 서브 ${sub}p`, en: `Main ${main}p / Sub ${sub}p`, ja: `メイン${main}p / サブ${sub}p` }),
  },
  {
    pattern: /^페이지 ([0-9,]+)$/,
    handler: ([, page]) => ({ ko: `페이지 ${page}`, en: `Page ${page}`, ja: `ページ ${page}` }),
  },
  {
    pattern: /^마이크 (.+)$/,
    handler: ([, value]) => ({ ko: `마이크 ${value}`, en: `Microphone ${value}`, ja: `マイク ${value}` }),
  },
  {
    pattern: /^카메라 (.+)$/,
    handler: ([, value]) => ({ ko: `카메라 ${value}`, en: `Camera ${value}`, ja: `カメラ ${value}` }),
  },
  {
    pattern: /^스피커 (.+)$/,
    handler: ([, value]) => ({ ko: `스피커 ${value}`, en: `Speaker ${value}`, ja: `スピーカー ${value}` }),
  },
  {
    pattern: /^약 ([0-9,]+)분$/,
    handler: ([, minutes]) => ({ ko: `약 ${minutes}분`, en: `about ${minutes} minutes`, ja: `約${minutes}分` }),
  },
  {
    pattern: /^약 ([0-9,]+)시간$/,
    handler: ([, hours]) => ({ ko: `약 ${hours}시간`, en: `about ${hours} hours`, ja: `約${hours}時間` }),
  },
  {
    pattern: /^약 ([0-9,]+)시간 ([0-9,]+)분$/,
    handler: ([, hours, minutes]) => ({ ko: `약 ${hours}시간 ${minutes}분`, en: `about ${hours} hours ${minutes} minutes`, ja: `約${hours}時間${minutes}分` }),
  },
  {
    pattern: /^화상회의 시작 (.+) 전$/,
    handler: ([, time]) => ({ ko: `화상회의 시작 ${time} 전`, en: `${time} before video meeting starts`, ja: `ビデオ会議開始${time}前` }),
  },
  {
    pattern: /^참석자-(.+)$/,
    handler: ([, id]) => ({ ko: `참석자-${id}`, en: `Participant-${id}`, ja: `参加者-${id}` }),
  },
  {
    pattern: /^가상참가자-(.+)$/,
    handler: ([, id]) => ({ ko: `가상참가자-${id}`, en: `Virtual participant-${id}`, ja: `仮想参加者-${id}` }),
  },
  {
    pattern: /^회의명 : (.+)$/,
    handler: ([, value]) => ({ ko: `회의명 : ${value}`, en: `Meeting name: ${value}`, ja: `会議名: ${value}` }),
  },
  {
    pattern: /^시간 : (.+)$/,
    handler: ([, value]) => ({ ko: `시간 : ${value}`, en: `Time: ${value}`, ja: `時間: ${value}` }),
  },
  {
    pattern: /^링크 : (.+)$/,
    handler: ([, value]) => ({ ko: `링크 : ${value}`, en: `Link: ${value}`, ja: `リンク: ${value}` }),
  },
  {
    pattern: /^암호 : (.+)$/,
    handler: ([, value]) => ({ ko: `암호 : ${value}`, en: `Password: ${value}`, ja: `パスワード: ${value}` }),
  },
  {
    pattern: /^회의 ID : (.+)$/,
    handler: ([, value]) => ({ ko: `회의 ID : ${value}`, en: `Meeting ID: ${value}`, ja: `会議ID: ${value}` }),
  },
  {
    pattern: /^([0-9]{4})년 ([0-9]{1,2})월 ([0-9]{1,2})일\((.+)\)(.*)$/,
    handler: ([, year, month, day, weekday, rest]) => ({ ko: `${year}년 ${month}월 ${day}일(${weekday})${rest}`, en: `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')} (${keep(weekday)})${rest}`, ja: `${year}年${month}月${day}日(${keep(weekday)})${rest}` }),
  },
];

export function translateByPattern(value: string, locale: AppLocale): string | null {
  for (const { pattern, handler } of patterns) {
    const matches = value.match(pattern);
    if (matches) {
      return handler(matches)[locale];
    }
  }
  return null;
}
